import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const AutoMagic2290 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="AutoMagic2290 - The Smarter Way to File your Form 2290"
          description="Discover how TaxZerone’s AutoMagic2290 tool simplifies and speeds up your Heavy Vehicle Use Tax filing, making the entire process faster and easier."
          keywords=""
          canonical="resources/excise-tax/automagic2290/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">AutoMagic2290 </span>
          </span>
          - Simplify Your Form 2290 Filing Process
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your HVUT Form the smart way with AutoMagic2290, just upload{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          your IRS-stamped Schedule 1 from last year.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=automagic`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/automagic2290/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        AutoMagic2290 at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              AutoMagic2290
                            </strong>{' '}
                            simplifies Form 2290 filing by importing business
                            and vehicle information from your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              prior-year IRS-stamped Schedule 1
                            </strong>
                            .
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            It eliminates repetitive data entry, helping you{' '}
                            <strong style={{ fontWeight: 600 }}>
                              save time and reduce manual errors
                            </strong>
                            , especially when filing multiple vehicles. You can{' '}
                            <strong style={{ fontWeight: 600 }}>
                              review and edit imported information
                            </strong>{' '}
                            and add new vehicles before submitting your return.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            After reviewing your vehicle and business details,
                            you can choose from available payment methods,
                            including{' '}
                            <Link
                              href="/resources/electronic-funds-withdrawal-efw/"
                              sx={{ color: '#0202ff' }}
                            >
                              EFW
                            </Link>
                            ,{' '}
                            <Link
                              href="/resources/eftps/"
                              sx={{ color: '#0202ff' }}
                            >
                              EFTPS
                            </Link>
                            , or{' '}
                            <strong style={{ fontWeight: 600 }}>
                              credit/debit card
                            </strong>
                            .
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="4"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            Once the IRS accepts your e-filed return, you can
                            receive your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              IRS-stamped Schedule 1 electronically
                            </strong>{' '}
                            in minutes.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Looking to make your Form 2290 filing easier and faster?
                      This resource page is your go-to guide for everything
                      related to{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_AUTOMAGIC_SITE_URL}?ref=automagic`}
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        AutoMagic2290
                      </Link>{' '}
                      - TaxZerone’s powerful tool that helps you to populate the
                      data from your previous filing with just one upload. Let’s
                      discuss it in detail!
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is AutoMagic2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whocan" sx={{ color: '#0202ff' }}>
                          Who can use AutoMagic2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why use Automagic2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#format" sx={{ color: '#0202ff' }}>
                          What File Formats will be accepted?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Add New Vehicles?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          How to file HVUT form with TaxZerone using
                          AutoMagic2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What Happens After Filing?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is AutoMagic2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  AutoMagic2290 is a smart feature offered by TaxZerone that
                  simplifies your IRS Form 2290 <br />
                  e-filing process. It automatically imports data from previous
                  year’s{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-schedule-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule 1
                  </Link>
                  , which will save you from entering vehicle and business
                  details manually.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whocan"
                >
                  Who can use AutoMagic2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 2290
                  </Link>{' '}
                  filers who have their IRS-stamped Schedule 1 PDF from the
                  previous year can use &apos;AutoMagic2290 &apos; to easily
                  pre-fill their data. This feature will be useful for reporting
                  multiple vehicles to the IRS, helping you avoid repetitive
                  data entry and ensuring an error-free process.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1, fontSize: 15 }}
                      >
                        Simplify your HVUT filing with AutoMagic2290 — fast,
                        easy, and error-free.
                      </Typography>
                      <Typography variant="body2">
                        Try it now and get your IRS-stamped Schedule 1 in
                        minutes!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=automagic`}
                      >
                        E-file now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="why"
                >
                  Why use AutoMagic2290?
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      No Manual Data Entry:
                    </span>{' '}
                    By uploading last year&apos;s schedule 1 to AutoMagic2290,
                    the tool will automatically fill in your business and
                    vehicle information, eliminating the need to enter
                    everything manually.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Save Time:</span> File
                    your form using AutoMagic2290 and it will take less than a
                    minute. Just review the information and submit your
                    application to the IRS.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Reduces Errors:</span> By
                    automatically transferring the details, the filing will be
                    accurate and minimize mistakes.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Perfect for multi-vehicle filing:
                    </span>{' '}
                    It will be more useful when filing multiple vehicles, as it
                    avoids repetitive data and ensures accurate filings.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="format"
                >
                  What File Formats will be accepted?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  AutoMagic2290 accepts{' '}
                  <span style={{ fontWeight: 600 }}> only PDF files</span> for
                  importing data from prior‑year Schedule 1 forms (the official
                  PDF issued by the IRS). If you try uploading any other file
                  type, the system won’t recognize it.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howto"
                >
                  How to Add New Vehicles?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  After importing the data, click on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    &quot;Add Taxable Vehicles&quot;
                  </span>{' '}
                  to include any new vehicle information, such as the VIN and
                  the taxable gross weight of the vehicle in the form.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="file"
                >
                  How to file HVUT form with TaxZerone using AutoMagic2290?
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Step 1:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Log in to your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=automagic`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    TaxZerone
                  </Link>{' '}
                  account and navigate to the Excise Tax Forms dashboard. Then,
                  click on the{' '}
                  <span style={{ fontWeight: 600 }}>“Dashboard”</span> button
                  and select the{' '}
                  <span style={{ fontWeight: 600 }}>“Start New Return”</span> to
                  begin your HVUT filing.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/dashboard-auto-img.webp" />
                  <source srcSet="/assets/images/dashboard-auto-img.png" />
                  <img
                    src="/assets/images/dashboard-auto-img.png"
                    alt="TaxZerone’s Home Application Dashboard"
                    width={767}
                    height={520}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, mt: 2, mb: 1 }}
                >
                  {' '}
                  Step 2:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>“Auto Magic 2290”</span>for
                  a faster and smarter filing experience.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-dash-img.webp" />
                  <source srcSet="/assets/images/automagic-dash-img.png" />
                  <img
                    src="/assets/images/automagic-dash-img.png"
                    alt="Select “Auto Magic 2290” to upload your prior year schedule 1 for a faster and smarter filing experience."
                    width={759}
                    height={496}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, mt: 2, mb: 1 }}
                >
                  {' '}
                  Step 3:{' '}
                </Typography>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mt: 2, mb: 1 }}
                >
                  <ShortcutIcon
                    sx={{
                      mb: -0.9,
                      direction: 'rotate 90deg',
                      mr: 0.5,
                      color: '#333',
                    }}
                  />
                  Select the tax year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-tax-img.webp" />
                  <source srcSet="/assets/images/automagic-tax-img.png" />
                  <img
                    src="/assets/images/automagic-tax-img.png"
                    alt="Form 2290: Select the tax period"
                    width={767}
                    height={83}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>

                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mt: 2, mb: 1 }}
                >
                  <ShortcutIcon sx={{ mb: -0.9, mr: 0.5, color: '#333' }} />
                  select the First Used Month (FUM)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-use-month-img.webp" />
                  <source srcSet="/assets/images/automagic-use-month-img.png" />
                  <img
                    src="/assets/images/automagic-use-month-img.png"
                    alt="Form 2290: Click on the month in which the vehicle was first used on public highways during the tax year."
                    width={758}
                    height={77}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mt: 2, mb: 1 }}
                >
                  <ShortcutIcon sx={{ mb: -0.7, color: '#333' }} /> Upload the
                  previous year&apos;s schedule 1 to pre-fill your details in
                  the current filing. You can review and edit details if any
                  changes are required.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-year-img.webp" />
                  <source srcSet="/assets/images/automagic-year-img.png" />
                  <img
                    src="/assets/images/automagic-year-img.png"
                    alt="Upload your prior-year form 2290 schedule 1 and it must be an original PDF received from the IRS."
                    width={739}
                    height={277}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, mt: 2, mb: 1 }}
                >
                  {' '}
                  Step 4:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select your preferred payment method to pay the IRS tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Electronic Funds Withdrawal (
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      EFW
                    </Link>
                    ):
                  </span>{' '}
                  It allows the IRS to directly debit from your bank account on
                  the date that mentioned.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Electronic Federal Tax Payment System (
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      EFTPS
                    </Link>
                    ):
                  </span>{' '}
                  you can schedule your payment separately on the IRS website.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Credit or Debit card:
                  </span>{' '}
                  You can pay using a credit or debit card through an approved
                  payment processor.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-efw-img.webp" />
                  <source srcSet="/assets/images/automagic-efw-img.png" />
                  <img
                    src="/assets/images/automagic-efw-imgpng"
                    alt="Form 2290: Select your preferred payment method such as EFW, EFTPS or Credit/Debit card to pay the IRS tax."
                    width={767}
                    height={53}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, mt: 2, mb: 1 }}
                >
                  {' '}
                  Step 5:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select any additional services you may need, then enter your
                  card details, and transmit the return securely to the IRS.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/automagic-card-img.webp" />
                  <source srcSet="/assets/images/automagic-card-img.png" />
                  <img
                    src="/assets/images/automagic-card-img.png"
                    alt="Form 2290: Enter your credit card details to make payment and click the Pay & E-File button to transmit the return to the IRS."
                    width={727}
                    height={579}
                    className="new-form-employ"
                    style={{ border: '2px solid #000' }}
                  />
                </picture>
                <Box
                  component="div"
                  sx={{
                    mt: 3,
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px !important',
                      }}
                    >
                      ðŸ’¡ Need a quick tour?
                    </Typography>
                    <Typography variant="body2">
                      Check out our easy step-by-step video on YouTube to see
                      how <span style={{ fontWeight: 600 }}>AutoMagic2290</span>{' '}
                      simplifies your Form 2290 filing
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    ðŸ‘‰
                    <span style={{ fontWeight: 600 }}>
                      Watch the video here:
                    </span>{' '}
                    <Link
                      href="https://youtu.be/Lp40_EqP1OI?si=f-60bmshEbT9dhCl"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      https://youtu.be/Lp40_EqP1OI?si=f-60bmshEbT9dhCl
                    </Link>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="happen"
                >
                  What Happens After Filing?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Once you e-file using AutoMagic2290:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    You will receive your IRS-stamped Schedule 1 instantly.
                  </li>
                  <li>
                    You can download, print, or email the document directly from
                    your TaxZerone account.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File with AutoMagic2290?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Skip the manual entry and speed up your Form 2290 filing with
              AutoMagic2290.
              <br />
              Upload your prior Schedule 1 and get your IRS-stamped Schedule 1
              within minutes.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=automagic`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default AutoMagic2290;
